<template>
	<v-dialog :value="value" @input="$emit('input', $event)" max-width="700" persistent>
		<v-card class="background-2 round-card">
			<v-card-title class="pa-3">
				<p class="pl-2 pl-xl-3">Importa dai File Condivisi</p>
				<v-spacer></v-spacer>
			</v-card-title>
			<v-divider></v-divider>
			<v-card-text class="pt-4 body-2" style="max-height: 500px; overflow-y: auto">
				<v-progress-circular v-if="loading" indeterminate color="primary" class="d-block mx-auto my-6"></v-progress-circular>
				<v-treeview
					v-else-if="items.length"
					v-model="selected"
					:items="items"
					:load-children="loadChildren"
					item-key="key"
					item-text="name"
					selectable
					selection-type="independent"
					open-on-click
					dense
				>
					<template v-slot:prepend="{ item }">
						<v-icon v-if="item.type === 'folder'" color="amber darken-2">mdi-folder</v-icon>
						<v-icon v-else :color="getFileColor(item.ext)">{{ getFileIcon(item.ext) }}</v-icon>
					</template>
					<template v-slot:append="{ item }">
						<span v-if="item.type === 'doc'" class="text-caption grey--text">{{ formatSize(item.size) }}</span>
					</template>
				</v-treeview>
				<div v-else class="text-center grey--text pa-6">
					Nessun documento nei File Condivisi
				</div>
			</v-card-text>
			<v-divider></v-divider>
			<v-card-actions>
				<span class="text-caption grey--text" v-if="selected.length">{{ selected.length }} selezionati</span>
				<v-spacer></v-spacer>
				<v-btn text color="primary" @click="close">Annulla</v-btn>
				<v-btn text color="info" @click="confirm" :loading="saving" :disabled="!selected.length">Importa</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
export default {
	name: 'LibraryImportDialog',
	props: {
		value: { type: Boolean, default: false },
		// base delle rotte DMS della company, es. 'portal-dms/12'
		apiBase: { type: String, required: true },
		// cartella DMS in cui atterrano i file importati (null = Home)
		targetFolderId: { type: Number, default: null }
	},
	data() {
		return {
			loading: false,
			saving: false,
			items: [],
			selected: []
		}
	},
	watch: {
		value(aperto) {
			if (aperto) this.loadRoot()
		}
	},
	methods: {
		async loadRoot() {
			this.selected = []
			this.items = []
			this.loading = true
			try {
				this.items = await this.fetchLevel(null)
			} catch (e) {
				// Senza segnalazione il dialog mostrerebbe "Nessun documento",
				// facendo passare un errore per una libreria vuota
				this.items = []
				this.$emit('error', this.getErrorMessage(e, 'Errore nel caricamento dei File Condivisi'))
			} finally {
				this.loading = false
			}
		},

		// Un livello della libreria: cartelle (nodi espandibili) + file (foglie)
		async fetchLevel(parentId) {
			var resp = await this.axios.get('drive/folders', { params: { parent_id: parentId } })
			if (!resp.data.success) return []

			var folders = (resp.data.data.folders || []).map(f => ({
				key: 'f-' + f.id,
				id: f.id,
				type: 'folder',
				name: f.name,
				children: []
			}))
			var documents = (resp.data.data.documents || []).map(d => ({
				key: 'd-' + d.id,
				id: d.id,
				type: 'doc',
				ext: d.ext,
				size: d.size,
				name: this.$options.filters.fileName(d.name, d.ext)
			}))

			return folders.concat(documents)
		},

		// L'errore va intercettato qui: se la promise rigetta, v-treeview lascia
		// il nodo con lo spinner acceso all'infinito
		async loadChildren(item) {
			try {
				item.children = await this.fetchLevel(item.id)
			} catch (e) {
				this.$emit('error', this.getErrorMessage(e, 'Errore nel caricamento della cartella'))
			}
		},

		async confirm() {
			this.saving = true
			try {
				var resp = await this.axios.post(this.apiBase + '/import-from-library', {
					folder_ids: this.idsByPrefix('f-'),
					attachment_ids: this.idsByPrefix('d-'),
					target_folder_id: this.targetFolderId
				})
				this.$emit('imported', resp.data.message || 'Importazione completata')
				this.close()
			} catch (e) {
				this.$emit('error', this.getErrorMessage(e, 'Errore nell\'importazione'))
			} finally {
				this.saving = false
			}
		},

		// Stesso helper di DmsMixin: il backend risponde con messaggi utili
		// (es. destinazione non valida, nessuna selezione)
		getErrorMessage(e, fallback) {
			if (e && e.response && e.response.data && e.response.data.message) {
				return e.response.data.message
			}
			return fallback || 'Errore'
		},

		// Le chiavi dei nodi sono prefissate per non confondere id cartella e id allegato
		idsByPrefix(prefix) {
			return this.selected
				.filter(key => key.indexOf(prefix) === 0)
				.map(key => Number(key.substring(prefix.length)))
		},

		close() {
			this.$emit('input', false)
		},

		// Copie degli helper di DmsMixin: il dialog non usa il mixin (che
		// richiede dmsConfig e tutto lo stato del DMS), ma le icone devono
		// restare identiche al resto del modulo
		getFileIcon(ext) {
			var map = {
				pdf: 'mdi-file-pdf-box', doc: 'mdi-file-word-box', docx: 'mdi-file-word-box',
				xls: 'mdi-file-excel-box', xlsx: 'mdi-file-excel-box',
				ppt: 'mdi-file-powerpoint-box', pptx: 'mdi-file-powerpoint-box',
				jpg: 'mdi-file-image', jpeg: 'mdi-file-image', png: 'mdi-file-image', gif: 'mdi-file-image', webp: 'mdi-file-image',
				zip: 'mdi-folder-zip', rar: 'mdi-folder-zip', '7z': 'mdi-folder-zip',
				txt: 'mdi-file-document', csv: 'mdi-file-delimited',
				xml: 'mdi-file-code', json: 'mdi-file-code',
				p7m: 'mdi-file-certificate', pfx: 'mdi-file-certificate',
				ovpn: 'mdi-folder-network'
			}
			return map[ext] || 'mdi-file'
		},

		getFileColor(ext) {
			var map = {
				pdf: 'red', doc: 'blue', docx: 'blue',
				xls: 'green', xlsx: 'green',
				ppt: 'orange', pptx: 'orange',
				jpg: 'purple', jpeg: 'purple', png: 'purple', gif: 'purple',
				zip: 'amber', p7m: 'teal'
			}
			return map[ext] || 'grey'
		},

		formatSize(bytes) {
			if (!bytes) return '0 B'
			var units = ['B', 'KB', 'MB', 'GB']
			var i = 0
			var size = Number(bytes)
			while (size >= 1024 && i < units.length - 1) { size /= 1024; i++ }
			return size.toFixed(i > 0 ? 1 : 0) + ' ' + units[i]
		}
	}
}
</script>
